import { AD_SLOTS } from "../../config/adSlots";
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  FormEvent,
} from "react";
import { useElementStore } from "../../store/elementStore";
import Enhanced3DElement from "../Enhanced3DElement";
import Arena from "../Arena";
import { loadUserProfile } from "../../services/storageService";
import { ChatComponent } from "../common/ChatComponent";
import { submitScore } from "../../services/firebaseService";
import { colors } from "../../theme/designTokens";
import { triggerHaptic } from "../../services/capacitorService";
import ParticleSystem from "../effects/ParticleSystem";
import { Swords, Trophy, Users, Clock, Zap, Crown, Target } from "lucide-react";

import { checkEmailVerified } from "../../services/firebaseService";
import { sendEmailVerification } from "firebase/auth";

import FunFact from '../FunFact';
import HintSystem from '../ui/HintSystem';
import AdBanner from "../ads/AdBanner";

import {
  auth,
  db,
  recordBattle,
  findBattleOpponent,
} from "../../services/firebaseService";
import {
  collection,
  setDoc,
  addDoc,
  updateDoc,
  doc,
  onSnapshot,
  getDoc,
  serverTimestamp,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { webRTCService, PeerMessage } from "../../services/webRTCService";
import VFXCanvas from "../VFXCanvas";

import { playSound } from "../../services/soundService";
import Celebration from "../Celebration";

// Define battle states
enum BattleState {
  WAITING = "waiting",
  MATCHMAKING = "matchmaking",
  INVITATION = "invitation",
  READY = "ready",
  IN_PROGRESS = "in_progress",
  COMPLETE = "complete",
}

// Invitation interface
type BattleInvitation = {
  id: string;
  senderId: string;
  senderName: string;
  senderAvatar?: string;
  recipientId: string;
  sessionId: string;
  targetElement: string;
  timestamp: any;
  status: "pending" | "accepted" | "declined" | "expired";
};

// Session interface
type BattleSession = {
  id: string;
  player1Id: string;
  player1Name: string;
  player1Avatar?: string;
  player2Id: string;
  player2Name: string;
  player2Avatar?: string;
  targetElement: string;
};

// Online user interface
type OnlineUser = {
  userId: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
  lastActive: any;
};

// Chat message interface
export type ChatMessage = {
  senderId: string;
  senderName: string;
  message: string;
  timestamp: number;
};

// Remote move interface
type RemoteMove = {
  elementId: string;
  zoneIndex: number;
  timestamp: number;
};

const BattleArena: React.FC = () => {
  const [battleState, setBattleState] = useState<BattleState>(
    BattleState.WAITING
  );
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [selectedOpponent, setSelectedOpponent] = useState<OnlineUser | null>(
    null
  );
  const [invitation, setInvitation] = useState<BattleInvitation | null>(null);
  const [battleSession, setBattleSession] = useState<BattleSession | null>(
    null
  );
  const [timer, setTimer] = useState<number>(0);
  const [playerProgress, setPlayerProgress] = useState<number>(0);
  const [opponentProgress, setOpponentProgress] = useState<number>(0);
  const [targetElement, setTargetElement] = useState<string>("");
  const [playerWon, setPlayerWon] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // New states for remote moves, chat, and UI controls
  const [remoteSelectedElements, setRemoteSelectedElements] = useState<
    Array<string | null>
  >([null, null]);
  const [remoteMoves, setRemoteMoves] = useState<RemoteMove[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [newChatMessage, setNewChatMessage] = useState<string>("");
  const [showChat, setShowChat] = useState<boolean>(false);

  const chatContainerRef = useRef<HTMLDivElement>(null);

  const [funFactData, setFunFactData] = useState<{
    ingredients: string[];
    result: string;
  } | null>(null);

  // 1. Add a ref for the chat input field
  const chatInputRef = useRef<HTMLInputElement>(null);

  const [celebrate, setCelebrate] = useState(false);
  const [showParticles, setShowParticles] = useState(false);

  const vfxRef = useRef<any>(null);

  const { all, unlocked, selected } = useElementStore();

  const [lastDiscovery, setLastDiscovery] = useState<string | null>(null);

  const select = useElementStore((s) => s.select);
  const discoverRecipe = useElementStore((s) => s.discoverRecipe);
  const unlockElement = useElementStore((s) => s.unlock);
  const clearSelection = useElementStore((s) => s.clearSelection);

  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  // 2. Add an effect to focus the input when chat is opened
  useEffect(() => {
    if (showChat && chatInputRef.current) {
      setTimeout(() => {
        chatInputRef.current?.focus();
      }, 300); // Small delay to allow animation to complete
    }
  }, [showChat]);

  // 3. Add keyboard shortcut handling
  const handleKeyDown1 = (e: React.KeyboardEvent) => {
    // Toggle chat with T key when in battle
    if (e.key === "t" && battleState === BattleState.IN_PROGRESS && !showChat) {
      e.preventDefault();
      setShowChat(true);
    }

    // ESC to close chat
    if (e.key === "Escape" && showChat) {
      setShowChat(false);
    }
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent | KeyboardEvent) => {
      // Handle global T key press to open chat only when chat is not already open
      if (
        e.key === "t" &&
        battleState === BattleState.IN_PROGRESS &&
        !showChat
      ) {
        e.preventDefault();
        setShowChat(true);

        // Focus the input field after opening chat
        setTimeout(() => {
          if (chatInputRef.current) {
            chatInputRef.current.focus();
          }
        }, 100);
      }

      // ESC to close chat
      if (e.key === "Escape" && showChat) {
        setShowChat(false);
      }
    },
    [battleState, showChat]
  );

  useEffect(() => {
    if (battleState === BattleState.IN_PROGRESS) {
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [battleState, handleKeyDown]);

  // Load user and init WebRTC
  useEffect(() => {
    const profile = loadUserProfile();
    const uid = profile.firebaseId;
    if (!uid) return; // early return if undefined

    (async () => {
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) {
        setCurrentUser(userDoc.data());
        webRTCService.init(uid);
      }
    })();
  }, []);

  // Scroll to bottom of chat when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current && chatMessages.length > 0) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [chatMessages]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      webRTCService.cleanup();
    };
  }, []);

  // Connection & message listeners
  useEffect(() => {
    if (!currentUser) return;

    const handleConnectionState = (state: RTCPeerConnectionState) => {
      // When the P2P link comes up, move the inviter out of MATCHMAKING into READY
      if (state === "connected" && battleState === BattleState.MATCHMAKING) {
        setBattleState(BattleState.READY);
      }
    };

    const handlePeerMessage = (msg: PeerMessage) => {
      switch (msg.type) {
        case "start":
          // Only the acceptor will see this – they go IN_PROGRESS when inviter clicks
          if (battleState === BattleState.READY) {
            setBattleState(BattleState.IN_PROGRESS);
            setTimer(180);
          }
          break;
        case "progress":
          setOpponentProgress(msg.data.progress);
          break;
        case "elementUnlocked":
          if (msg.data.elementId === targetElement) {
            setOpponentProgress(100);
            if (battleState === BattleState.IN_PROGRESS) {
              setBattleState(BattleState.COMPLETE);
              setPlayerWon(false);
              finishBattle(false);
            }
          }
          break;
        case "complete":
          setBattleState(BattleState.COMPLETE);
          setPlayerWon(msg.data.winner === currentUser.userId);
          finishBattle(msg.data.winner === currentUser.userId);
          break;
        case "move":
          // Handle incoming move data from the remote player
          handleRemoteMove(msg.data);
          break;
        case "chat":
          // Handle incoming chat message
          const newMessage: ChatMessage = {
            senderId: msg.data.senderId,
            senderName: msg.data.senderName,
            message: msg.data.message,
            timestamp: msg.timestamp,
          };
          setChatMessages((prev) => [...prev, newMessage]);
          break;
        default:
          console.log("Unknown message type:", msg.type);
      }
    };

    webRTCService.addConnectionStateListener(handleConnectionState);
    webRTCService.addMessageListener(handlePeerMessage);
    return () => {
      webRTCService.removeConnectionStateListener(handleConnectionState);
      webRTCService.removeMessageListener(handlePeerMessage);
    };
  }, [battleState, currentUser, targetElement]);

  // Handle remote player moves
  const handleRemoteMove = (moveData: any) => {
    if (moveData.type === "select") {
      // Update remote selection state
      setRemoteSelectedElements((prev) => {
        const newSelection = [...prev];
        newSelection[moveData.zoneIndex] = moveData.elementId;
        return newSelection;
      });

      // Add to moves history
      setRemoteMoves((prev) => [
        ...prev,
        {
          elementId: moveData.elementId,
          zoneIndex: moveData.zoneIndex,
          timestamp: Date.now(),
        },
      ]);
    } else if (moveData.type === "clear") {
      // Clear remote selection
      setRemoteSelectedElements([null, null]);
    } else if (moveData.type === "discover") {
      // Handle when remote player discovers something
      if (moveData.result) {
        // Flash or indicate the discovered element somehow
      }
      setRemoteSelectedElements([null, null]);
    }
  };

  // Manage online users
  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, "onlineUsers"),
      orderBy("lastActive", "desc"),
      limit(10)
    );
    const unsub = onSnapshot(q, (snap) => {
      const users: OnlineUser[] = [];
      snap.forEach((doc) => {
        const u = doc.data() as OnlineUser;
        if (u.userId !== currentUser.userId) users.push(u);
      });
      setOnlineUsers(users);
    });
    const setOnline = async () => {
      await setDoc(
        doc(db, "onlineUsers", currentUser.userId),
        { ...currentUser, lastActive: serverTimestamp() },
        { merge: true }
      );
    };
    setOnline();
    const iv = setInterval(setOnline, 60000);
    return () => {
      unsub();
      clearInterval(iv);
      deleteDoc(doc(db, "onlineUsers", currentUser.userId));
    };
  }, [currentUser]);

  // Invitations listener
  useEffect(() => {
    if (!currentUser) return;
    const invQ = query(
      collection(db, "battleInvitations"),
      where("recipientId", "==", currentUser.userId),
      where("status", "==", "pending"),
      limit(1)
    );
    const unsub = onSnapshot(invQ, (snap) => {
      snap.docChanges().forEach((ch) => {
        if (ch.type === "added") {
          const inv = { id: ch.doc.id, ...ch.doc.data() } as BattleInvitation;
          setInvitation(inv);
          setBattleState(BattleState.INVITATION);
        }
      });
    });
    return () => unsub();
  }, [currentUser]);

  // Record result
  const finishBattle1 = useCallback(
    async (won: boolean) => {
      await recordBattle({
        player1Id: battleSession?.player1Id || currentUser?.userId,
        player1Name: battleSession?.player1Name || currentUser?.displayName,
        player2Id: battleSession?.player2Id || selectedOpponent?.userId || "",
        player2Name:
          battleSession?.player2Name || selectedOpponent?.displayName || "",
        winner: won ? currentUser?.userId : battleSession?.player1Id || "",
        mode: "battle",
        score1: playerProgress,
        score2: opponentProgress,
      });
    },
    [
      battleSession,
      currentUser,
      selectedOpponent,
      playerProgress,
      opponentProgress,
    ]
  );

  // inside your BattleArena component:
  const finishBattle = useCallback(
    async (won: boolean) => {
      // 1) record the match result in your 'battles' collection
      await recordBattle({
        player1Id: battleSession?.player1Id || currentUser?.userId,
        player1Name: battleSession?.player1Name || currentUser?.displayName,
        player2Id: battleSession?.player2Id || selectedOpponent?.userId || "",
        player2Name:
          battleSession?.player2Name || selectedOpponent?.displayName || "",
        winner: won ? currentUser!.userId : battleSession?.player1Id || "",
        mode: "battle",
        score1: playerProgress,
        score2: opponentProgress,
      });

      // 2) submit a leaderboard entry if this user is signed in
      const profile = loadUserProfile();
      if (profile.firebaseId) {
        const yourId = profile.firebaseId;
        // determine opponent’s id/name
        const oppId =
          battleSession?.player1Id === yourId
            ? battleSession!.player2Id
            : battleSession!.player1Id;
        const oppName =
          battleSession?.player1Id === yourId
            ? battleSession!.player2Name
            : battleSession!.player1Name;

        await submitScore({
          userId: yourId,
          username: profile.username,
          displayName: profile.displayName,
          avatarUrl: profile.avatarUrl,
          gameMode: "battle",
          score: won ? 1 : 0, // 1 win vs 0 loss
          timestamp: {
            seconds: Math.floor(Date.now() / 1000),
            nanoseconds: 0,
          },
          opponentId: oppId,
          opponentName: oppName,
          winnerId: won ? yourId : oppId,
        }).catch(console.error);
      }
    },
    [
      battleSession,
      currentUser,
      selectedOpponent,
      playerProgress,
      opponentProgress,
    ]
  );

  // Local unlock detection
  useEffect(() => {
    if (
      battleState === BattleState.IN_PROGRESS &&
      unlocked.has(targetElement)
    ) {
      setPlayerProgress(100);
      webRTCService.sendMessage("elementUnlocked", {
        elementId: targetElement,
      });
      webRTCService.sendMessage("complete", { winner: currentUser.userId });
      setBattleState(BattleState.COMPLETE);
      finishBattle(true);
    }
  }, [unlocked, battleState, targetElement, currentUser?.userId, finishBattle]);

  // Timer
  useEffect(() => {
    let iv: number;
    if (battleState === BattleState.IN_PROGRESS && timer > 0) {
      iv = window.setInterval(() => setTimer((t) => t - 1), 1000);
    } else if (timer === 0 && battleState === BattleState.IN_PROGRESS) {
      const won = playerProgress > opponentProgress;
      webRTCService.sendMessage("complete", {
        winner: won ? currentUser.userId : null,
      });
      setBattleState(BattleState.COMPLETE);
      finishBattle(won);
    }
    return () => clearInterval(iv);
  }, [battleState, timer]);

  // Challenge an opponent
  const sendBattleInvitation = async (opp: OnlineUser) => {
    if (!currentUser) return;

    // 1. Prepare opponent and UI
    setSelectedOpponent(opp);
    setBattleState(BattleState.MATCHMAKING);

    // 2. Pick a random target element
    const targets = Object.keys(all).filter((id) => all[id].recipe);
    const tgt = targets[Math.floor(Math.random() * targets.length)];
    setTargetElement(tgt);

    // 3. Create a WebRTC session (writes to webrtcSessions/{sessionId})
    const sessionId = await webRTCService.createBattleSession({
      targetElement: tgt,
    });

    // 4. Populate inviter's local session state so the READY screen can render
    setBattleSession({
      id: sessionId,
      player1Id: currentUser.userId,
      player1Name: currentUser.displayName,
      player1Avatar: currentUser.avatarUrl,
      player2Id: opp.userId,
      player2Name: opp.displayName,
      player2Avatar: opp.avatarUrl,
      targetElement: tgt,
    });

    // 5. Post the invitation in Firestore for the recipient
    const invRef = await addDoc(collection(db, "battleInvitations"), {
      senderId: currentUser.userId,
      senderName: currentUser.displayName,
      senderAvatar: currentUser.avatarUrl,
      recipientId: opp.userId,
      sessionId,
      targetElement: tgt,
      timestamp: serverTimestamp(),
      status: "pending",
    });

    // 6. Expire the invitation if not accepted in 30s
    setTimeout(async () => {
      if (battleState === BattleState.MATCHMAKING) {
        await updateDoc(doc(db, "battleInvitations", invRef.id), {
          status: "expired",
        });
        setBattleState(BattleState.WAITING);
        setSelectedOpponent(null);
        setErrorMessage("Invitation expired. Try again.");
        setTimeout(() => setErrorMessage(null), 5000);
      }
    }, 30000);
  };

  // Accept or decline invitation
  const respondToInvitation = async (accept: boolean) => {
    if (!invitation || !currentUser) return;
    await updateDoc(doc(db, "battleInvitations", invitation.id), {
      status: accept ? "accepted" : "declined",
    });
    if (accept) {
      setTargetElement(invitation.targetElement);
      const details = await webRTCService.joinBattleSession(
        invitation.sessionId
      );
      setBattleSession({
        id: invitation.sessionId,
        player1Id: invitation.senderId,
        player1Name: invitation.senderName,
        player1Avatar: invitation.senderAvatar,
        player2Id: currentUser.userId,
        player2Name: currentUser.displayName,
        player2Avatar: currentUser.avatarUrl,
        targetElement: invitation.targetElement,
      });
      setBattleState(BattleState.READY);
    } else {
      setBattleState(BattleState.WAITING);
    }
    setInvitation(null);
  };

  // Ready screen Start button
  const startBattle = () => {
    // Move yourself into IN_PROGRESS and kick off your timer
    setBattleState(BattleState.IN_PROGRESS);
    setTimer(180);
    setChatMessages([]); // Clear chat for new session

    // Tell your peer to start at the same moment
    webRTCService.sendMessage("start", {});
  };

  // Element select handler with move broadcasting
  const handleElementSelect = useCallback(
    (id: string, zoneIndex: number) => {
      select(id, zoneIndex);
      playSound("select");

      // Broadcast the move to peer
      if (battleState === BattleState.IN_PROGRESS) {
        webRTCService.sendMessage("move", {
          type: "select",
          elementId: id,
          zoneIndex: zoneIndex,
        });
      }
    },
    [select, battleState]
  );

  // memoize to keep the ref stable
  const handleVfxComplete = useCallback(() => {
    clearSelection();
    // any other “after effect” logic…
  }, [clearSelection]);

  // Updated handleCombine function with VFX support
  const handleCombine = useCallback(() => {
    if (battleState !== BattleState.IN_PROGRESS) return;

    // Get the elements being combined
    const ingredients = selected.filter(Boolean);

    if (ingredients.length < 2) {
      playSound("error");
      // Need at least 2 ingredients to combine
      return;
    }

    playSound("combine");

    // Try to discover a recipe
    const result = discoverRecipe(ingredients);
  

    // Broadcast the combine action to peer
    webRTCService.sendMessage("move", {
      type: "discover",
      ingredients: ingredients,
      result: result,
    });

    if (result) {
      // Found a valid combination!
      // 🎉 record last discovery
      setLastDiscovery(result);
      setTimeout(() => setLastDiscovery(null), 4000);
      setFunFactData({ ingredients, result });
      setCelebrate(true);
      playSound("discover");
      // Play combine effect animation
      if (vfxRef.current && vfxRef.current.playCombineEffect) {
        console.log("Playing VFX combine effect for:", result);
        vfxRef.current.playCombineEffect(result);
      }

      // Unlock the element in your local store
      unlockElement(result);

      // Battle progress impact
      const inc = Math.random() * 10 + 15; // 15-25% increase for successful combinations
      setPlayerProgress((prev) => {
        const next = Math.min(100, prev + inc);
        webRTCService.sendMessage("progress", { progress: next });
        return next;
      });

      // Show success message
      setErrorMessage("New element discovered!");
      setTimeout(() => setErrorMessage(null), 2000);
    } else {
      // No valid combination

      // Small progress increment even for failures
      const inc = Math.random() * 5 + 2; // 2-7% increment
      setPlayerProgress((prev) => {
        const next = Math.min(100, prev + inc);
        webRTCService.sendMessage("progress", { progress: next });
        return next;
      });

      // Show error message
      playSound("error");
      setErrorMessage("Invalid combination!");
      setTimeout(() => setErrorMessage(null), 2000);
    }

    // Clear the selection zones
    //clearSelection();

    // Broadcast the clear action
    webRTCService.sendMessage("move", {
      type: "clear",
    });
  }, [battleState, selected, discoverRecipe, unlockElement]);

  const sendChatMessage = () => {
    if (
      !newChatMessage.trim() ||
      !currentUser ||
      battleState !== BattleState.IN_PROGRESS
    )
      return;

    const message: ChatMessage = {
      senderId: currentUser.userId,
      senderName: currentUser.displayName,
      message: newChatMessage.trim(),
      timestamp: Date.now(),
    };

    // Add to local chat state
    setChatMessages((prev) => [...prev, message]);

    // Send to peer
    webRTCService.sendMessage("chat", {
      senderId: message.senderId,
      senderName: message.senderName,
      message: message.message,
    });

    // Clear input and keep focus
    setNewChatMessage("");

    // Make sure the chat input maintains focus after sending
    setTimeout(() => {
      if (chatInputRef.current) {
        chatInputRef.current.focus();
      }
    }, 0);
  };

  // Handle chat input submission

  const handleChatSubmit1 = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newChatMessage.trim()) return;
    // use sendMessage with type 'chat'
    webRTCService.sendMessage("chat", newChatMessage);
    setChatMessages((prev) => [
      ...prev,
      {
        senderId: currentUser?.userId,
        senderName: currentUser.displayName,
        message: newChatMessage,
        timestamp: Date.now(),
      },
    ]);
    setNewChatMessage("");
  };

  const handleChatSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newChatMessage.trim()) return;

    // build the proper payload
    const payload = {
      senderId: currentUser!.userId,
      senderName: currentUser!.displayName,
      message: newChatMessage.trim(),
    };

    // 1️⃣ Add it locally
    setChatMessages((prev) => [...prev, { ...payload, timestamp: Date.now() }]);

    // 2️⃣ Send it to the peer as an object
    webRTCService.sendMessage("chat", payload);

    // 3️⃣ Clear input
    setNewChatMessage("");
  };

  // Helper functions
  const formatTime = (s: number) =>
    `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
  const getAvatar = (u: any) =>
    u?.avatarUrl ||
    `https://api.dicebear.com/6.x/personas/svg?seed=${u.userId}`;

  // Remote player view (simplified Arena)
  const RemotePlayerView = () => {
    return (
      <div className="flex-1 bg-gray-900 p-4 rounded-md">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center">
            <img
              src={getAvatar(
                battleSession?.player1Id === currentUser?.userId
                  ? {
                      userId: battleSession?.player2Id,
                      avatarUrl: battleSession?.player2Avatar,
                    }
                  : {
                      userId: battleSession?.player1Id,
                      avatarUrl: battleSession?.player1Avatar,
                    }
              )}
              alt="Opponent"
              className="w-8 h-8 rounded-full mr-2"
            />
            <span className="text-gray-300 text-sm">
              {battleSession?.player1Id === currentUser?.userId
                ? battleSession?.player2Name
                : battleSession?.player1Name}
              's View
            </span>
          </div>
          <div className="text-gray-300 text-sm">
            {opponentProgress.toFixed(0)}% Complete
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          {/* Selection zones */}
          {remoteSelectedElements.map((elementId, idx) => (
            <div
              key={`remote-zone-${idx}`}
              className="bg-gray-800 rounded-md flex items-center justify-center h-20 border border-gray-700"
            >
              {elementId ? (
                <Enhanced3DElement e={all[elementId]} size={48} intensity="subtle" />
              ) : null}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto">
          {/* Recently used elements (simplified) */}
          {Array.from(
            new Set(remoteMoves.slice(-8).map((m) => m.elementId))
          ).map((elementId) => (
            <div key={`recent-${elementId}`} className="flex justify-center">
              <Enhanced3DElement e={all[elementId]} size={36} intensity="subtle" />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Updated ChatComponent function with proper z-index and positioning
  // const ChatComponent = () => {
  //   return (
  //     <div
  //       className={`fixed bottom-0 right-0 w-72 md:w-80 ${
  //         showChat ? "h-64 md:h-96" : "h-12"
  //       } bg-gray-800 border border-gray-700 rounded-tl-md shadow-lg transition-all duration-300 flex flex-col z-50`}
  //       onKeyDown={(e) => e.stopPropagation()} // Prevent keyboard shortcuts triggering when typing
  //     >
  //       {/* Chat header */}
  //       <div
  //         className="flex justify-between items-center p-3 bg-gray-700 rounded-tl-md cursor-pointer"
  //         onClick={() => setShowChat(!showChat)}
  //       >
  //         <div className="flex items-center">
  //           <span className="text-white font-medium">Battle Chat</span>
  //           {!showChat && chatMessages.length > 0 && (
  //             <span className="ml-2 bg-blue-600 text-white text-xs rounded-full px-2 py-0.5">
  //               {chatMessages.length}
  //             </span>
  //           )}
  //         </div>
  //         <button className="text-gray-300 hover:text-white">
  //           {showChat ? "▼" : "▲"}
  //         </button>
  //       </div>

  //       {/* Chat messages */}
  //       {showChat && (
  //         <>
  //           <div
  //             ref={chatContainerRef}
  //             className="flex-1 overflow-y-auto p-3 space-y-2"
  //           >
  //             {chatMessages.length === 0 ? (
  //               <div className="text-gray-500 text-center text-sm p-2">
  //                 <p>No messages yet</p>
  //                 <p className="text-xs mt-1">Press T to quickly open chat</p>
  //               </div>
  //             ) : (
  //               chatMessages.map((msg, idx) => (
  //                 <div
  //                   key={`msg-${idx}-${msg.timestamp}`}
  //                   className={`p-2 rounded-md max-w-[80%] ${
  //                     msg.senderId === currentUser?.userId
  //                       ? "bg-blue-800 ml-auto"
  //                       : "bg-gray-700"
  //                   }`}
  //                 >
  //                   <div className="text-xs text-gray-400">
  //                     {msg.senderName}
  //                   </div>
  //                   <div className="text-white break-words">{msg.message}</div>
  //                 </div>
  //               ))
  //             )}
  //           </div>

  //           {/* Chat input */}
  //           <form
  //             onSubmit={handleChatSubmit}
  //             className="p-2 border-t border-gray-700 flex"
  //           >
  //             <input
  //               ref={chatInputRef}
  //               type="text"
  //               value={newChatMessage}
  //               onChange={(e) => setNewChatMessage(e.target.value)}
  //               onKeyDown={(e) => e.stopPropagation()} // Prevent keyboard shortcuts while typing
  //               placeholder="Type message..."
  //               className="flex-1 bg-gray-700 text-white p-2 rounded-l-md focus:outline-none focus:ring-1 focus:ring-blue-500"
  //             />
  //             <button
  //               type="submit"
  //               className="bg-blue-600 hover:bg-blue-700 text-white px-3 rounded-r-md"
  //             >
  //               Send
  //             </button>
  //           </form>
  //         </>
  //       )}
  //     </div>
  //   );
  // };

  // UI for each state
  if (battleState === BattleState.WAITING) {
    return (
      <div
        className="flex-1 flex flex-col items-center justify-center p-6 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${colors.background.primary} 0%, #1a1f3a 50%, #2d1b4e 100%)`,
          minHeight: '100vh',
        }}
      >
        <ParticleSystem
          trigger={showParticles}
          type="celebration"
          onComplete={() => setShowParticles(false)}
        />

        {/* Animated background elements */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            background: `radial-gradient(circle at 20% 50%, ${colors.brand.secondary} 0%, transparent 50%), radial-gradient(circle at 80% 80%, ${colors.brand.accent} 0%, transparent 50%)`,
            animation: 'pulse 8s ease-in-out infinite',
          }}
        />

        <div className="relative z-10 max-w-4xl w-full">
          {/* Header */}
          <div className="text-center mb-8 animate-fadeIn">
            <div className="flex items-center justify-center mb-4">
              <Swords
                size={48}
                style={{
                  color: colors.brand.secondary,
                  filter: `drop-shadow(0 0 20px ${colors.brand.secondary})`,
                  animation: 'float 3s ease-in-out infinite',
                }}
              />
            </div>
            <h1
              className="text-5xl font-extrabold mb-2"
              style={{
                background: colors.gradients.secondary,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Battle Arena
            </h1>
            <p style={{ color: colors.text.secondary, fontSize: '1.1rem' }}>
              Challenge other alchemists to epic duels!
            </p>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div
              style={{
                background: 'rgba(239, 68, 68, 0.2)',
                border: '2px solid #EF4444',
                borderRadius: '16px',
                padding: '16px',
                marginBottom: '24px',
                color: '#FCA5A5',
                textAlign: 'center',
                animation: 'slideIn 0.3s ease-out',
              }}
            >
              {errorMessage}
            </div>
          )}

          {!currentUser ? (
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '24px',
                padding: '48px',
                textAlign: 'center',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                animation: 'scaleIn 0.4s ease-out',
              }}
            >
              <Users size={64} style={{ color: colors.brand.accent, margin: '0 auto 24px' }} />
              <p style={{ color: colors.text.secondary, fontSize: '1.2rem', marginBottom: '24px' }}>
                Please sign in to challenge opponents and compete in the arena!
              </p>
              <button
                onClick={() => triggerHaptic('medium')}
                style={{
                  padding: '14px 32px',
                  borderRadius: '12px',
                  border: 'none',
                  background: colors.gradients.secondary,
                  color: 'white',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: `0 4px 20px ${colors.brand.secondary}40`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 6px 25px ${colors.brand.secondary}60`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 4px 20px ${colors.brand.secondary}40`;
                }}
              >
                Sign In to Battle
              </button>
            </div>
          ) : (
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '24px',
                padding: '32px',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                animation: 'scaleIn 0.4s ease-out',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                <Users size={28} style={{ color: colors.brand.accent, marginRight: '12px' }} />
                <h3 style={{ color: colors.text.primary, fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
                  Online Alchemists
                </h3>
                <div
                  style={{
                    marginLeft: 'auto',
                    background: colors.elementTiers[2].color + '30',
                    color: colors.elementTiers[2].color,
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                  }}
                >
                  {onlineUsers.length} Online
                </div>
              </div>

              {onlineUsers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                  <Swords
                    size={64}
                    style={{
                      color: colors.text.tertiary,
                      opacity: 0.5,
                      margin: '0 auto 16px',
                    }}
                  />
                  <p style={{ color: colors.text.secondary, fontSize: '1.1rem' }}>
                    No alchemists online right now.
                  </p>
                  <p style={{ color: colors.text.tertiary, fontSize: '0.9rem', marginTop: '8px' }}>
                    Check back later to find worthy opponents!
                  </p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {onlineUsers.map((u, idx) => (
                    <div
                      key={u.userId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'rgba(255, 255, 255, 0.03)',
                        borderRadius: '16px',
                        padding: '16px',
                        border: '2px solid rgba(255, 255, 255, 0.1)',
                        transition: 'all 0.3s ease',
                        animation: `slideIn 0.4s ease-out ${idx * 0.1}s both`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                        e.currentTarget.style.transform = 'translateX(4px)';
                        e.currentTarget.style.borderColor = colors.brand.secondary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                        e.currentTarget.style.transform = 'translateX(0)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ position: 'relative' }}>
                          <img
                            src={getAvatar(u)}
                            alt={u.displayName}
                            style={{
                              width: '56px',
                              height: '56px',
                              borderRadius: '50%',
                              marginRight: '16px',
                              border: `3px solid ${colors.brand.secondary}`,
                              boxShadow: `0 0 20px ${colors.brand.secondary}40`,
                            }}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              bottom: 0,
                              right: '16px',
                              width: '14px',
                              height: '14px',
                              background: colors.elementTiers[2].color,
                              borderRadius: '50%',
                              border: '2px solid ' + colors.background.primary,
                              boxShadow: `0 0 10px ${colors.elementTiers[2].color}`,
                            }}
                          />
                        </div>
                        <div>
                          <div style={{ color: colors.text.primary, fontSize: '1.1rem', fontWeight: '600' }}>
                            {u.displayName}
                          </div>
                          <div style={{ color: colors.text.tertiary, fontSize: '0.9rem' }}>
                            @{u.username}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          triggerHaptic('medium');
                          sendBattleInvitation(u);
                        }}
                        style={{
                          padding: '10px 24px',
                          borderRadius: '12px',
                          border: 'none',
                          background: colors.gradients.secondary,
                          color: 'white',
                          fontSize: '1rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: `0 4px 16px ${colors.brand.secondary}40`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = `0 6px 20px ${colors.brand.secondary}60`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = `0 4px 16px ${colors.brand.secondary}40`;
                        }}
                      >
                        <Swords size={18} />
                        Challenge
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <style>
          {`
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translateY(-20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            @keyframes scaleIn {
              from {
                opacity: 0;
                transform: scale(0.9);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }

            @keyframes slideIn {
              from {
                opacity: 0;
                transform: translateX(-20px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }

            @keyframes pulse {
              0%, 100% {
                opacity: 0.1;
              }
              50% {
                opacity: 0.2;
              }
            }

            @keyframes float {
              0%, 100% {
                transform: translateY(0px);
              }
              50% {
                transform: translateY(-10px);
              }
            }
          `}
        </style>
      </div>
    );
  }

  if (battleState === BattleState.MATCHMAKING && selectedOpponent) {
    return (
      <div
        className="flex-1 flex items-center justify-center p-6 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${colors.background.primary} 0%, #1a1f3a 50%, #2d1b4e 100%)`,
          minHeight: '100vh',
        }}
      >
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '32px',
            padding: '48px',
            textAlign: 'center',
            maxWidth: '500px',
            backdropFilter: 'blur(10px)',
            border: `2px solid ${colors.brand.secondary}`,
            boxShadow: `0 20px 60px ${colors.brand.secondary}40`,
            animation: 'scaleIn 0.4s ease-out',
          }}
        >
          <div style={{ marginBottom: '24px', animation: 'pulse 2s ease-in-out infinite' }}>
            <Clock
              size={64}
              style={{
                color: colors.brand.accent,
                filter: `drop-shadow(0 0 20px ${colors.brand.accent})`,
              }}
            />
          </div>
          <h2
            style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              marginBottom: '16px',
              background: colors.gradients.secondary,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Challenge Sent!
          </h2>
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: '24px' }}>
            <img
              src={getAvatar(selectedOpponent)}
              alt={selectedOpponent.displayName}
              style={{
                width: '96px',
                height: '96px',
                borderRadius: '50%',
                border: `4px solid ${colors.brand.secondary}`,
                boxShadow: `0 0 40px ${colors.brand.secondary}60`,
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                width: '32px',
                height: '32px',
                background: colors.gradients.secondary,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            >
              <Swords size={16} color="white" />
            </div>
          </div>
          <p style={{ color: colors.text.secondary, fontSize: '1.2rem', marginBottom: '32px' }}>
            Waiting for{' '}
            <span
              style={{
                color: colors.brand.secondary,
                fontWeight: '600',
              }}
            >
              {selectedOpponent.displayName}
            </span>{' '}
            to accept your challenge...
          </p>
          <button
            onClick={() => {
              triggerHaptic('light');
              setBattleState(BattleState.WAITING);
            }}
            style={{
              padding: '12px 32px',
              borderRadius: '12px',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.05)',
              color: colors.text.primary,
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Cancel Challenge
          </button>
        </div>
      </div>
    );
  }

  if (battleState === BattleState.INVITATION && invitation) {
    return (
      <div
        className="flex-1 flex items-center justify-center p-6 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${colors.background.primary} 0%, #1a1f3a 50%, #2d1b4e 100%)`,
          minHeight: '100vh',
        }}
      >
        <ParticleSystem trigger={true} type="celebration" onComplete={() => {}} />
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '32px',
            padding: '48px',
            textAlign: 'center',
            maxWidth: '550px',
            backdropFilter: 'blur(10px)',
            border: `3px solid ${colors.brand.secondary}`,
            boxShadow: `0 20px 60px ${colors.brand.secondary}60`,
            animation: 'scaleIn 0.4s ease-out',
          }}
        >
          <div style={{ marginBottom: '24px', animation: 'float 3s ease-in-out infinite' }}>
            <Swords
              size={72}
              style={{
                color: colors.brand.secondary,
                filter: `drop-shadow(0 0 30px ${colors.brand.secondary})`,
              }}
            />
          </div>
          <h2
            style={{
              fontSize: '2.5rem',
              fontWeight: 'bold',
              marginBottom: '24px',
              background: colors.gradients.secondary,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Battle Challenge!
          </h2>
          <img
            src={invitation.senderAvatar || getAvatar(invitation)}
            alt={invitation.senderName}
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              margin: '0 auto 24px',
              border: `4px solid ${colors.brand.secondary}`,
              boxShadow: `0 0 40px ${colors.brand.secondary}80`,
            }}
          />
          <p style={{ color: colors.text.secondary, fontSize: '1.3rem', marginBottom: '32px' }}>
            <span
              style={{
                color: colors.brand.accent,
                fontWeight: '700',
                fontSize: '1.4rem',
              }}
            >
              {invitation.senderName}
            </span>
            <br />
            has challenged you to a duel!
          </p>
          {targetElement && all[targetElement] && (
            <div
              style={{
                marginBottom: '32px',
                padding: '24px',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '20px',
                border: '2px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <p style={{ color: colors.text.tertiary, fontSize: '0.9rem', marginBottom: '12px' }}>
                Target Element:
              </p>
              <Enhanced3DElement e={all[invitation.targetElement]} size={80} intensity="medium" />
              <div
                style={{
                  color: colors.text.primary,
                  fontSize: '1.3rem',
                  fontWeight: '600',
                  marginTop: '12px',
                }}
              >
                {all[invitation.targetElement].name}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
            <button
              onClick={() => {
                triggerHaptic('medium');
                respondToInvitation(true);
              }}
              style={{
                padding: '14px 32px',
                borderRadius: '12px',
                border: 'none',
                background: colors.gradients.success,
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: `0 4px 20px ${colors.brand.success}40`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = `0 6px 25px ${colors.brand.success}60`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 4px 20px ${colors.brand.success}40`;
              }}
            >
              Accept Challenge
            </button>
            <button
              onClick={() => {
                triggerHaptic('light');
                respondToInvitation(false);
              }}
              style={{
                padding: '14px 32px',
                borderRadius: '12px',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.05)',
                color: colors.text.primary,
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (battleState === BattleState.READY && currentUser && battleSession) {
    return (
      <div
        className="flex-1 flex items-center justify-center p-6 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${colors.background.primary} 0%, #1a1f3a 50%, #2d1b4e 100%)`,
          minHeight: '100vh',
        }}
      >
        {/* Animated VS background */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '15rem',
            fontWeight: 'bold',
            color: 'rgba(255, 255, 255, 0.02)',
            zIndex: 0,
            animation: 'pulse 3s ease-in-out infinite',
          }}
        >
          VS
        </div>

        <ParticleSystem trigger={true} type="celebration" onComplete={() => {}} />

        <div
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '32px',
            padding: '48px',
            textAlign: 'center',
            maxWidth: '700px',
            width: '100%',
            backdropFilter: 'blur(10px)',
            border: `3px solid ${colors.brand.secondary}`,
            boxShadow: `0 20px 60px ${colors.brand.secondary}60`,
            animation: 'scaleIn 0.4s ease-out',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <h2
            style={{
              fontSize: '2.5rem',
              fontWeight: 'bold',
              marginBottom: '40px',
              background: colors.gradients.legendary,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Battle Ready!
          </h2>

          {/* Players VS section */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '40px',
              marginBottom: '40px',
            }}
          >
            {/* Player 1 */}
            <div
              style={{
                textAlign: 'center',
                animation: 'slideIn 0.5s ease-out',
              }}
            >
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={getAvatar(currentUser)}
                  alt="You"
                  style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    marginBottom: '16px',
                    border: `4px solid ${colors.brand.primary}`,
                    boxShadow: `0 0 30px ${colors.brand.primary}80`,
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: '12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: colors.brand.primary,
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    boxShadow: `0 4px 12px ${colors.brand.primary}60`,
                  }}
                >
                  YOU
                </div>
              </div>
              <div
                style={{
                  color: colors.text.primary,
                  fontSize: '1.2rem',
                  fontWeight: '600',
                }}
              >
                {currentUser.displayName}
              </div>
            </div>

            {/* VS divider */}
            <div
              style={{
                fontSize: '4rem',
                fontWeight: 'bold',
                background: colors.gradients.secondary,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'float 3s ease-in-out infinite',
                filter: `drop-shadow(0 0 20px ${colors.brand.secondary})`,
              }}
            >
              VS
            </div>

            {/* Player 2 */}
            <div
              style={{
                textAlign: 'center',
                animation: 'slideIn 0.5s ease-out 0.1s both',
              }}
            >
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={
                    battleSession.player1Id === currentUser.userId
                      ? getAvatar({
                          userId: battleSession.player2Id,
                          avatarUrl: battleSession.player2Avatar,
                        })
                      : getAvatar({
                          userId: battleSession.player1Id,
                          avatarUrl: battleSession.player1Avatar,
                        })
                  }
                  alt="Opponent"
                  style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    marginBottom: '16px',
                    border: `4px solid ${colors.brand.error}`,
                    boxShadow: `0 0 30px ${colors.brand.error}80`,
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: '12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: colors.brand.error,
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    boxShadow: `0 4px 12px ${colors.brand.error}60`,
                  }}
                >
                  OPPONENT
                </div>
              </div>
              <div
                style={{
                  color: colors.text.primary,
                  fontSize: '1.2rem',
                  fontWeight: '600',
                }}
              >
                {battleSession.player1Id === currentUser.userId
                  ? battleSession.player2Name
                  : battleSession.player1Name}
              </div>
            </div>
          </div>

          {/* Target element */}
          <div
            style={{
              marginBottom: '40px',
              padding: '24px',
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '20px',
              border: '2px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <p
              style={{
                color: colors.text.tertiary,
                fontSize: '0.9rem',
                marginBottom: '16px',
              }}
            >
              Race to create:
            </p>
            {targetElement && all[targetElement] && (
              <>
                <Enhanced3DElement e={all[targetElement]} size={96} intensity="intense" />
                <div
                  style={{
                    color: colors.brand.accent,
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    marginTop: '12px',
                  }}
                >
                  {all[targetElement].name}
                </div>
              </>
            )}
          </div>

          {/* Start battle button */}
          <button
            onClick={() => {
              triggerHaptic('heavy');
              startBattle();
            }}
            style={{
              padding: '16px 48px',
              borderRadius: '16px',
              border: 'none',
              background: colors.gradients.legendary,
              color: 'white',
              fontSize: '1.3rem',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: `0 8px 30px ${colors.brand.accent}60`,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)';
              e.currentTarget.style.boxShadow = `0 12px 40px ${colors.brand.accent}80`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = `0 8px 30px ${colors.brand.accent}60`;
            }}
          >
            ⚔️ Start Battle!
          </button>
        </div>
      </div>
    );
  }

  if (battleState === BattleState.IN_PROGRESS) {
    return (
      <div
        className="flex-1 flex flex-col"
        style={{
          background: `linear-gradient(135deg, ${colors.background.primary} 0%, #1a1f3a 100%)`,
        }}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        {/* Top-left: last discovered element */}
        {lastDiscovery && (
          <div
            style={{
              position: 'absolute',
              top: 'max(env(safe-area-inset-top), 1rem)',
              left: 'max(env(safe-area-inset-left), 1rem)',
              zIndex: 20,
              display: 'flex',
              alignItems: 'center',
              background: 'rgba(0, 0, 0, 0.8)',
              backdropFilter: 'blur(10px)',
              padding: '8px 16px',
              borderRadius: '12px',
              border: `2px solid ${colors.brand.success}`,
              boxShadow: `0 4px 20px ${colors.brand.success}40`,
              animation: 'slideIn 0.3s ease-out',
            }}
          >
            <Enhanced3DElement e={all[lastDiscovery]} size={32} intensity="subtle" />
            <span style={{ color: colors.text.primary, marginLeft: '12px', fontWeight: '600' }}>
              {all[lastDiscovery].name}
            </span>
          </div>
        )}

        {/* Top header bar */}
        <div
          style={{
            background: `linear-gradient(90deg, ${colors.background.secondary}, rgba(101, 163, 13, 0.1))`,
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `2px solid ${colors.brand.secondary}40`,
            backdropFilter: 'blur(10px)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Target size={20} style={{ color: colors.brand.accent }} />
            <span style={{ color: colors.text.tertiary, fontSize: '0.9rem' }}>Target:</span>
            {targetElement && all[targetElement] && (
              <>
                <Enhanced3DElement e={all[targetElement]} size={36} intensity="subtle" />
                <span style={{ color: colors.brand.accent, fontWeight: '700', fontSize: '1.1rem' }}>
                  {all[targetElement].name}
                </span>
              </>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '8px 16px',
              borderRadius: '12px',
            }}
          >
            <Clock size={20} style={{ color: colors.brand.warning }} />
            <span style={{ color: colors.text.primary, fontSize: '1.2rem', fontWeight: '700' }}>
              {formatTime(timer)}
            </span>
          </div>
        </div>

        {/* Main game container with split view */}
        <div className="flex-1 flex flex-col md:flex-row">
          {/* Your arena (top in mobile, left in desktop) */}
          <div className="flex-1 min-h-[50vh] md:min-h-0">
            <div className="h-full relative">
              {/* Error/success message */}
              {errorMessage && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 px-4 py-2 rounded-lg text-center">
                  <div
                    className={`p-3 rounded-lg ${
                      errorMessage.includes("Invalid")
                        ? "bg-red-900 bg-opacity-80 text-red-100"
                        : "bg-green-900 bg-opacity-80 text-green-100"
                    }`}
                  >
                    {errorMessage}
                  </div>
                </div>
              )}
              <Arena
                customSelectHandler={handleElementSelect}
                customCombineHandler={handleCombine}
              />

              {/* Add VFXCanvas with ref */}
              <VFXCanvas
                ref={vfxRef}
                onEffectComplete={handleVfxComplete}
                className="absolute inset-0 z-10 pointer-events-none"
              />

              <Celebration
                trigger={celebrate}
                onComplete={() => setCelebrate(false)}
              />
            </div>
          </div>

          {/* Opponent's arena (bottom in mobile, right in desktop) */}
          <div className="h-64 md:w-1/3 md:h-auto border-t-2 md:border-t-0 md:border-l-2 border-gray-700">
            <RemotePlayerView />
          </div>
        </div>

        {/* Bottom control bar */}
        <div
          style={{
            background: `linear-gradient(90deg, ${colors.background.secondary}, rgba(236, 72, 153, 0.1))`,
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: `2px solid ${colors.brand.secondary}40`,
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <button
            onClick={() => {
              triggerHaptic('medium');
              handleCombine();
            }}
            style={{
              padding: '12px 32px',
              borderRadius: '12px',
              border: 'none',
              background: colors.gradients.secondary,
              color: 'white',
              fontSize: '1.1rem',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: `0 4px 16px ${colors.brand.secondary}40`,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 6px 20px ${colors.brand.secondary}60`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `0 4px 16px ${colors.brand.secondary}40`;
            }}
          >
            <Zap size={20} />
            Combine
          </button>

          {/* Progress bars */}
          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ display: 'flex', gap: '24px', justifyContent: 'center' }}>
              {/* Your progress */}
              <div style={{ flex: 1, maxWidth: '200px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ color: colors.text.secondary, fontSize: '0.85rem', fontWeight: '600' }}>You</span>
                  <span style={{ color: colors.brand.primary, fontSize: '0.9rem', fontWeight: '700' }}>
                    {playerProgress.toFixed(0)}%
                  </span>
                </div>
                <div
                  style={{
                    height: '8px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${playerProgress}%`,
                      background: colors.gradients.primary,
                      borderRadius: '8px',
                      transition: 'width 0.3s ease',
                      boxShadow: `0 0 10px ${colors.brand.primary}`,
                    }}
                  />
                </div>
              </div>

              {/* Opponent progress */}
              <div style={{ flex: 1, maxWidth: '200px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ color: colors.text.secondary, fontSize: '0.85rem', fontWeight: '600' }}>Opponent</span>
                  <span style={{ color: colors.brand.error, fontSize: '0.9rem', fontWeight: '700' }}>
                    {opponentProgress.toFixed(0)}%
                  </span>
                </div>
                <div
                  style={{
                    height: '8px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${opponentProgress}%`,
                      background: colors.gradients.fire,
                      borderRadius: '8px',
                      transition: 'width 0.3s ease',
                      boxShadow: `0 0 10px ${colors.brand.error}`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={() => {
              triggerHaptic('light');
              setShowChat(!showChat);
            }}
            style={{
              padding: '12px 20px',
              borderRadius: '12px',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              background: 'rgba(255, 255, 255, 0.1)',
              color: colors.text.primary,
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            }}
          >
            <span>Chat</span>
            {chatMessages.length > 0 && (
              <span
                style={{
                  background: colors.brand.info,
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                }}
              >
                {chatMessages.length}
              </span>
            )}
            <span>{showChat ? "▼" : "▲"}</span>
          </button>
        </div>

        {funFactData && (
        <FunFact
          ingredients={funFactData.ingredients}
          result={funFactData.result}
        />
      )}

        {/* Chat component */}
        <ChatComponent
          showChat={showChat}
          setShowChat={setShowChat}
          chatMessages={chatMessages}
          newChatMessage={newChatMessage}
          setNewChatMessage={setNewChatMessage}
          handleChatSubmit={handleChatSubmit}
          chatInputRef={chatInputRef}
          chatContainerRef={chatContainerRef}
          currentUser={currentUser}
        />

        {/* Hint System */}
        <HintSystem unlockedElements={Array.from(unlocked)} />

        {/* Gameplay Banner Ad - Fixed at bottom */}
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            background: 'rgba(17, 24, 39, 0.95)',
            borderTop: '1px solid rgba(75, 85, 99, 0.3)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <AdBanner
            adSlot={AD_SLOTS.gameplay}
            adFormat="horizontal"
            fullWidthResponsive={true}
            style={{ maxHeight: '90px', margin: '0 auto' }}
          />
        </div>
      </div>
    );
  }

  if (battleState === BattleState.COMPLETE) {
    return (
      <div
        className="flex-1 flex items-center justify-center p-6 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${colors.background.primary} 0%, #1a1f3a 50%, #2d1b4e 100%)`,
          minHeight: '100vh',
        }}
      >
        {playerWon && <ParticleSystem trigger={true} type="celebration" onComplete={() => {}} />}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '32px',
            padding: '48px',
            textAlign: 'center',
            maxWidth: '600px',
            backdropFilter: 'blur(10px)',
            border: `3px solid ${playerWon ? colors.brand.success : colors.brand.error}`,
            boxShadow: `0 20px 60px ${playerWon ? colors.brand.success : colors.brand.error}60`,
            animation: 'scaleIn 0.4s ease-out',
          }}
        >
          <div style={{ marginBottom: '24px', animation: 'float 3s ease-in-out infinite' }}>
            {playerWon ? (
              <Crown
                size={96}
                style={{
                  color: colors.brand.accent,
                  filter: `drop-shadow(0 0 30px ${colors.brand.accent})`,
                }}
              />
            ) : (
              <Trophy
                size={96}
                style={{
                  color: colors.text.tertiary,
                  filter: `drop-shadow(0 0 20px ${colors.text.tertiary})`,
                  opacity: 0.6,
                }}
              />
            )}
          </div>
          <h2
            style={{
              fontSize: '3rem',
              fontWeight: 'bold',
              marginBottom: '24px',
              background: playerWon ? colors.gradients.success : colors.gradients.fire,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {playerWon ? "Victory!" : "Defeat"}
          </h2>
          <p style={{ color: colors.text.secondary, fontSize: '1.2rem', marginBottom: '32px' }}>
            {playerWon ? "You won the battle! Excellent alchemy skills!" : "Don't give up! Practice makes perfect!"}
          </p>
          {targetElement && all[targetElement] && (
            <div
              style={{
                marginBottom: '32px',
                padding: '20px',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '20px',
                border: '2px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <p style={{ color: colors.text.tertiary, fontSize: '0.9rem', marginBottom: '12px' }}>
                Target Element:
              </p>
              <Enhanced3DElement e={all[targetElement]} size={80} intensity="medium" />
              <div style={{ color: colors.text.primary, fontSize: '1.2rem', fontWeight: '600', marginTop: '12px' }}>
                {all[targetElement].name}
              </div>
            </div>
          )}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              marginBottom: '32px',
              padding: '24px',
              background: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '16px',
            }}
          >
            <div>
              <div style={{ color: colors.text.tertiary, fontSize: '0.9rem', marginBottom: '8px' }}>
                Your Progress
              </div>
              <div
                style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: playerWon ? colors.brand.success : colors.text.primary,
                }}
              >
                {playerProgress.toFixed(0)}%
              </div>
            </div>
            <div>
              <div style={{ color: colors.text.tertiary, fontSize: '0.9rem', marginBottom: '8px' }}>
                Opponent's Progress
              </div>
              <div
                style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: !playerWon ? colors.brand.error : colors.text.primary,
                }}
              >
                {opponentProgress.toFixed(0)}%
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {selectedOpponent && (
              <button
                onClick={() => {
                  triggerHaptic('medium');
                  sendBattleInvitation(selectedOpponent);
                }}
                style={{
                  padding: '14px 32px',
                  borderRadius: '12px',
                  border: 'none',
                  background: colors.gradients.secondary,
                  color: 'white',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: `0 4px 20px ${colors.brand.secondary}40`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = `0 6px 25px ${colors.brand.secondary}60`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 4px 20px ${colors.brand.secondary}40`;
                }}
              >
                <Zap size={20} />
                Rematch
              </button>
            )}
            <button
              onClick={() => {
                triggerHaptic('light');
                setBattleState(BattleState.WAITING);
              }}
              style={{
                padding: '14px 32px',
                borderRadius: '12px',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.05)',
                color: colors.text.primary,
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Back to Lobby
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full bg-gray-800 p-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
      <div className="text-white text-lg">Loading Battle Arena...</div>
    </div>
  );
};

export default BattleArena;
